import {
  clearExpandedTemplateDetails,
  fetchSelctivityTemplates,
  setTemplateMaintenanceGridDataState,
} from '@/features/selectivitySlice';
import { useAppDispatch, useAppSelector } from '@/hooks/customHooks';
import {
  Grid,
  GridCellProps,
  GridColumn,
  GridColumnMenuCheckboxFilter,
  GridColumnMenuProps,
  GridNoRecords,
} from '@progress/kendo-react-grid';
import React, { useContext, useEffect, useState } from 'react';
import { process as kendoProcess } from '@progress/kendo-data-query';
import { Pager } from '@progress/kendo-react-data-tools';
import GridColumnMenu from '../message-center/GridColumnMenu';
import { isColumnActive } from '@/common/gridHelpers';
import GridCellDate from '../message-center/GridCellDate';
import { SWrapper } from '../message-center/styles/MessageList.styled';
import GridTemplateDetailRow from './GridTemplateDetailRow';
import LoaderOverlay from '@/presentation/loader/LoaderOverlay';
import { SelectedGridContext } from './SelectivityTemplateMaintenance';
import { SelectivityTemplateMaintenanceGridColumns } from './SelectivityTemplateMaintenanceGridColumns';
import StickyBar from '../sticky-bar/StickyBar';
import Link from '@/presentation/link';
import ModalDialog from '../shared/modal/ModalDialog';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashCan } from '@fortawesome/pro-light-svg-icons';
import Api from '@/api/helpers';
import { DeleteSelectivityTemplateRequest } from '@/api/client';
import { customToast } from '../shared/Toasts';
import { setAppliedFisrtLevelSysPrins, setPreviousResultWithoutPagination } from '@/features/customersSlice';
import CustomExpandTemplateGridCell from '@/presentation/grid/TemplateGridCellExpand';

const SelectivityTemplateMaintenanceGrid: React.FC = () => {
  const [isConfirmationModalOpen, setConfirmationModalOpen] = useState<boolean>(false);
  const activeDocType = useAppSelector((state) => state.docTypes.activeDocType);
  const activeCustomer = useAppSelector((state) => state.customers.activeCustomer);
  const isLoadingCustomers = useAppSelector((state) => state.customers.customers.isRequestActive);
  const isLoadingDocTypes = useAppSelector((state) => state.docTypes.isRequestActive);
  const selectivityState = useAppSelector((state) => state.selectivity.selectivityTemplates);
  const selectivityTemplatesGridData = useAppSelector((state) => state.selectivity.selectivityTemplates.templateList);
  const selectivityTemplatesGridDataState = useAppSelector(
    (state) => state.selectivity.selectivityTemplates.gridDataState,
  );
  const dispatch = useAppDispatch();
  const {
    SELECTED_FIELD,
    selectedState,
    onHeaderSelectionChange,
    nrOfSelectedItems,
    onSelectionChange,
    DATA_ITEM_KEY,
    idGetter,
    //getSelectedPks,
    setSelectedState,
  } = useContext(SelectedGridContext);
  const instances = useAppSelector((state) => state.auth.availableInstances);
  const permissions = useAppSelector((state) => state.auth.permissions);
  const [behaviors, setBehaviors] = useState<string[]>();
  const [canUserDeleteTemplate, setCanUserDeleteTemplate] = useState({ tooltip: 'Delete', linkDisable: false });

  useEffect(() => {
    return () => {
      dispatch(clearExpandedTemplateDetails());
      dispatch(setAppliedFisrtLevelSysPrins(false));
      dispatch(setPreviousResultWithoutPagination([]));
    };
  }, []);

  useEffect(() => {
    setBehaviors(
      permissions.find((perm) => perm.databaseTarget === instances?.find((inst) => inst.isActive)?.target)?.behaviors,
    );
  }, [instances]);

  useEffect(() => {
    if (activeCustomer && activeDocType && activeDocType.primaryKey) {
      dispatch(
        fetchSelctivityTemplates({
          docTypeId: activeDocType?.primaryKey,
          sysPrins: [activeCustomer?.sysPrin],
        }),
      );
    }
  }, [activeCustomer, activeDocType]);

  useEffect(() => {
    const templateIdsUserCantDelete: string[] = [];
    Object.keys(selectedState).forEach((templateId) => {
      const selTemplate = selectivityState.templateList.find(
        (template) => template.primaryKey === parseInt(templateId),
      );
      if (!selTemplate?.canUserDeleteThisTemplate) {
        templateIdsUserCantDelete.push(templateId);
      }
    });

    if (nrOfSelectedItems > 0 && templateIdsUserCantDelete.length === Object.keys(selectedState).length) {
      setCanUserDeleteTemplate({
        tooltip: `User doesn't have permission to delete any of the selected templates`,
        linkDisable: true,
      });
    } else if (nrOfSelectedItems > 0 && templateIdsUserCantDelete.length) {
      let toolTipTemplateIds = templateIdsUserCantDelete.slice(0, 6).join(', ');
      if (templateIdsUserCantDelete.length > 6)
        toolTipTemplateIds = toolTipTemplateIds + ` and ${templateIdsUserCantDelete.length - 6} more`;
      setCanUserDeleteTemplate({
        tooltip: `User doesn't have permission to permanently delete Template ID: ${toolTipTemplateIds}`,
        linkDisable: true,
      });
    } else
      setCanUserDeleteTemplate({
        tooltip: ``,
        linkDisable: false,
      });
  }, [selectedState]);

  // every time the list of selected templates change, refresh the selected template list.
  useEffect(() => {
    setSelectedState({});
  }, [selectivityState.templateList]);

  const resultsWithoutPagination = kendoProcess(selectivityTemplatesGridData, {
    ...selectivityTemplatesGridDataState,
    skip: undefined,
    take: undefined,
  }).data;

  const result = kendoProcess(resultsWithoutPagination, {
    skip: selectivityTemplatesGridDataState.skip,
    take: selectivityTemplatesGridDataState.take,
  }).data;

  const getColumnCell = (column: typeof GridColumn) => {
    if (column.filter === 'date') {
      // eslint-disable-next-line react/prop-types
      return (props: GridCellProps) =>
        GridCellDate({ date: new Date(props.dataItem[column.field]), includeTimeStamp: true });
    } else {
      return null;
    }
  };

  const getHeaderSelectionValue = () => {
    return (
      result.findIndex((item) => selectedState[idGetter(item)]) !== -1 &&
      Object.keys(selectedState).length === result.length
    );
  };

  const confirmTemplateDeleteAction = async () => {
    const templateCount = selectedTemplateCount();

    try {
      const client = Api.getSelectivityClient(dispatch);
      if (activeCustomer && activeDocType && activeDocType.primaryKey) {
        await client.deleteSelectivityTemplates(
          new DeleteSelectivityTemplateRequest({
            sysPrin: activeCustomer?.sysPrin,
            docTypeId: activeDocType?.primaryKey,
            selectivityTemplateIds: selectedMessagesIds(),
          }),
        );
        customToast({
          content: `${templateCount} template(s) successfully deleted.`,
          type: 'success',
          id: 'tstDeleteTemplateSuccess',
        });
      }
    } finally {
      if (activeCustomer && activeDocType && activeDocType.primaryKey) {
        dispatch(
          fetchSelctivityTemplates({
            docTypeId: activeDocType?.primaryKey,
            sysPrins: [activeCustomer?.sysPrin],
          }),
        );
      }
      setConfirmationModalOpen(false);
    }
  };

  const DeleteTemplateModalTitle = () => {
    return `Delete selectivity template${selectedTemplateCount() > 1 ? 's' : ''}`;
  };

  const selectedTemplateCount = () => {
    return Object.values(selectedState).filter((x) => x === true).length;
  };

  const DeleteTemplatePrompt = () => {
    return `Are you sure you want to delete ${selectedTemplateCount()} selectivity template${
      selectedTemplateCount() > 1 ? 's' : ''
    }?`;
  };

  const selectedMessagesIds = () =>
    Object.keys(selectedState)
      .filter((key) => selectedState[key])
      .map(Number);

  return (
    <SWrapper>
      <Grid
        detail={GridTemplateDetailRow}
        expandField="expanded"
        data={
          isLoadingCustomers || isLoadingDocTypes || selectivityState.isRequestActive
            ? []
            : result.map((item) => ({
                ...item,
                [SELECTED_FIELD]: selectedState[idGetter(item)],
              }))
        }
        className="ml__kendo-grid"
        resizable
        pageable
        reorderable
        pager={() => (
          <Pager
            skip={selectivityTemplatesGridDataState.skip!}
            take={selectivityTemplatesGridDataState.take!}
            pageSizes={[10, 20, 50, 100]}
            buttonCount={3}
            previousNext
            onPageChange={(e) => {
              dispatch(setTemplateMaintenanceGridDataState({ ...selectivityTemplatesGridDataState, ...e }));
              setSelectedState({});
            }}
            total={resultsWithoutPagination.length}
          />
        )}
        {...selectivityTemplatesGridDataState}
        sortable
        dataItemKey={DATA_ITEM_KEY}
        selectedField={SELECTED_FIELD}
        onSelectionChange={onSelectionChange}
        onHeaderSelectionChange={onHeaderSelectionChange}
        selectable={{
          enabled: true,
          drag: false,
          cell: false,
          mode: 'multiple',
        }}
        onDataStateChange={(e) => {
          setSelectedState({});
          dispatch(setTemplateMaintenanceGridDataState(e.dataState));
        }}
      >
        <GridNoRecords>
          {isLoadingCustomers || isLoadingDocTypes || selectivityState.isRequestActive ? (
            <div style={{ marginTop: '65vh' }}>
              <LoaderOverlay sizePx={50} borderPx={3} />
            </div>
          ) : undefined}
        </GridNoRecords>

        <GridColumn
          resizable={false}
          field={SELECTED_FIELD}
          width="45px"
          headerSelectionValue={getHeaderSelectionValue()}
          reorderable={false}
          orderIndex={0}
          locked={true}
        />

        <GridColumn width="40px" field="expanded" cell={CustomExpandTemplateGridCell} title="" />

        {SelectivityTemplateMaintenanceGridColumns.map(
          (column) =>
            column.show && (
              <GridColumn
                id={`lbl${column.title.replace(/ /g, '')}`}
                minResizableWidth={100}
                key={column.field}
                field={column.field}
                title={column.title}
                width={column.width}
                show={column.show}
                filter={column.filter}
                cell={getColumnCell(column)}
                isColumnActive={() => isColumnActive(column.field, selectivityTemplatesGridDataState)}
                headerClassName={isColumnActive(column.field, selectivityTemplatesGridDataState) ? 'active' : ''}
                columnMenu={(props: GridColumnMenuProps) => {
                  return column.field === 'status' ? (
                    <GridColumnMenuCheckboxFilter {...props} data={result} expanded={true} searchBox={() => null} />
                  ) : (
                    <GridColumnMenu
                      gridColumnProps={props}
                      noColumnMenu={column.noColumnMenu}
                      notFilterable={column.notFilterable}
                      twoFilterCriteria={column.twoFilterCriteria}
                    />
                  );
                }}
              />
            ),
        )}
      </Grid>
      <StickyBar selectedItemType="template" selectedCount={nrOfSelectedItems} setSelectedState={setSelectedState}>
        <div className="last-with-margin">
          <Link
            id="lnkDeleteTemplate"
            icon="faTrashCan"
            noUnderline
            title={canUserDeleteTemplate.tooltip ? canUserDeleteTemplate.tooltip : 'Delete Template'}
            disabled={
              nrOfSelectedItems === 0 ||
              canUserDeleteTemplate.linkDisable ||
              (behaviors?.includes('ReadOnly') && behaviors?.length === 1)
            }
            label="Delete Template"
            onClick={() => {
              setConfirmationModalOpen(true);
            }}
          />
        </div>
      </StickyBar>
      <ModalDialog
        id="modalDeleteTemplate"
        showPrompt={isConfirmationModalOpen}
        onClose={() => {
          setConfirmationModalOpen(false);
        }}
        icon={<FontAwesomeIcon icon={faTrashCan} />}
        title={DeleteTemplateModalTitle()}
        modalButtons={[
          {
            label: 'Cancel',
            action: () => setConfirmationModalOpen(false),
            color: { color: 'secondary' },
            id: 'btnCancel',
          },
          {
            label: 'Delete',
            action: confirmTemplateDeleteAction,
            color: { color: 'primary' },
            id: 'btnDelete',
          },
        ]}
      >
        <div>
          <p className="important-description">{DeleteTemplatePrompt()}</p>
        </div>
      </ModalDialog>
    </SWrapper>
  );
};

export default SelectivityTemplateMaintenanceGrid;
